import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createApiCall } from 'services'
import type { Address, AddressPayload } from 'types'

// When VITE_SKIP_AUTH=true, persist addresses to localStorage so the Address
// Book is fully exercisable without a running API (mirrors useUserPreferences).
const SKIP_AUTH = import.meta.env.VITE_SKIP_AUTH === 'true'
const LOCAL_KEY = 'vera:addresses'

export const ADDRESSES_QUERY_KEY = ['company', 'addresses']

const makeId = (): string => {
  try {
    if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID()
  } catch {
    /* fall through */
  }
  return `addr_${Date.now()}_${Math.floor(Math.random() * 1e6)}`
}

const readLocal = (): Address[] => {
  try {
    const raw = window.localStorage.getItem(LOCAL_KEY)
    return raw ? (JSON.parse(raw) as Address[]) : []
  } catch {
    return []
  }
}

const writeLocal = (rows: Address[]) => {
  try {
    window.localStorage.setItem(LOCAL_KEY, JSON.stringify(rows))
  } catch {
    /* ignore */
  }
}

const sortAddresses = (rows: Address[]): Address[] =>
  [...rows].sort((a, b) => Number(b.is_default) - Number(a.is_default))

export const useAddresses = () => {
  const query = useQuery({
    queryKey: ADDRESSES_QUERY_KEY,
    queryFn: async (): Promise<Address[]> => {
      if (SKIP_AUTH) return sortAddresses(readLocal())
      const response = await createApiCall({ method: 'GET', url: '/addresses' })
      const data = (response.data?.data ?? []) as Address[]
      return sortAddresses(data)
    },
    staleTime: 1000 * 60,
    refetchOnWindowFocus: false,
  })

  return {
    addresses: query.data ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    refetch: query.refetch,
  }
}

export const useAddressMutations = () => {
  const queryClient = useQueryClient()
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ADDRESSES_QUERY_KEY })

  const create = useMutation<Address, Error, AddressPayload>({
    mutationFn: async payload => {
      if (SKIP_AUTH) {
        const rows = readLocal()
        const next: Address = {
          id: makeId(),
          company_id: 'local',
          type: payload.type,
          label: payload.label ?? null,
          full_name: payload.full_name,
          country: payload.country,
          state: payload.state ?? null,
          city: payload.city,
          postal_code: payload.postal_code,
          address_line: payload.address_line,
          latitude: payload.latitude ?? null,
          longitude: payload.longitude ?? null,
          place_id: payload.place_id ?? null,
          formatted_address: payload.formatted_address ?? null,
          is_default: Boolean(payload.is_default),
          created_at: new Date().toISOString(),
        }
        const cleared = next.is_default ? rows.map(r => ({ ...r, is_default: false })) : rows
        writeLocal([...cleared, next])
        return next
      }
      const response = await createApiCall({ method: 'POST', url: '/addresses', data: payload })
      return response.data as Address
    },
    onSuccess: invalidate,
  })

  const update = useMutation<Address, Error, { id: string; payload: AddressPayload }>({
    mutationFn: async ({ id, payload }) => {
      if (SKIP_AUTH) {
        let rows = readLocal()
        if (payload.is_default) rows = rows.map(r => ({ ...r, is_default: false }))
        rows = rows.map(r => (r.id === id ? { ...r, ...payload, label: payload.label ?? null, state: payload.state ?? null, is_default: Boolean(payload.is_default) } : r))
        writeLocal(rows)
        return rows.find(r => r.id === id) as Address
      }
      const response = await createApiCall({ method: 'PUT', url: `/addresses/${id}`, data: payload })
      return response.data as Address
    },
    onSuccess: invalidate,
  })

  const remove = useMutation<void, Error, { id: string }>({
    mutationFn: async ({ id }) => {
      if (SKIP_AUTH) {
        writeLocal(readLocal().filter(r => r.id !== id))
        return
      }
      await createApiCall({ method: 'DELETE', url: `/addresses/${id}` })
    },
    onSuccess: invalidate,
  })

  const setDefault = useMutation<Address | void, Error, { id: string }>({
    mutationFn: async ({ id }) => {
      if (SKIP_AUTH) {
        writeLocal(readLocal().map(r => ({ ...r, is_default: r.id === id })))
        return
      }
      const response = await createApiCall({ method: 'PUT', url: `/addresses/${id}/default` })
      return response.data as Address
    },
    onSuccess: invalidate,
  })

  return {
    create: create.mutateAsync,
    update: update.mutateAsync,
    remove: remove.mutateAsync,
    setDefault: setDefault.mutateAsync,
    isSaving: create.isPending || update.isPending,
    isRemoving: remove.isPending,
    isSettingDefault: setDefault.isPending,
  }
}
